import { isEmpty } from 'lodash';
import React, { memo } from 'react';
import styled, { keyframes, css } from 'styled-components';

import { fromUnixTime, isAfter } from 'date-fns';
import Bubble from '../../../Bubble';
import { formatDate } from '../../../../utils/date';

import TimeFrom from '../../Common/TimeFrom';

import { Explosion as ExplosionIcon } from '@icons/common';

import { TICKET_TYPES } from '../../../../constants';

import {
  PRIORITIES,
  TRANSLATE_PRIORITIES,
  COLORS_BY_PRIORITY,
  TICKET_STATUS_PLACEMENT,
  TITLES,
  STATUSES,
  EXTRA_STATUSES,
  TIME_FORMAT,
} from './constants';
import { ValueOf } from '@utils/tsUtils';
import { CloseReasonType, PlanReasonType } from '../types';

export interface ITicketStatus {
  className?: string;
  priority: ValueOf<typeof PRIORITIES>;
  time?: number | null;
  title: string;
  isOption?: boolean;
  orderIds?: string[] | null;
  closeReason?: CloseReasonType | null;
  planReason?: PlanReasonType | null;
  type?: ValueOf<typeof TICKET_TYPES>;
  placement?: ValueOf<typeof TICKET_STATUS_PLACEMENT>;
  onClick?: () => void;
}

const TicketStatus = ({
  priority = PRIORITIES.MEDIUM,
  time = null,
  type = TICKET_TYPES.UNKNOWN,
  title: serverTitle,
  isOption = false,
  planReason = null,
  closeReason = null,
  orderIds = null,
  className = '',
  placement,
  ...props
}: ITicketStatus) => {
  const today = new Date();
  const statusTime = time ? fromUnixTime(time) : null;

  let titleLabel: string | JSX.Element = TITLES[serverTitle]
    ? TITLES[serverTitle]
    : TITLES[STATUSES.OPEN];

  const isDisabledBubble =
    placement !== TICKET_STATUS_PLACEMENT.TICKET_CARD &&
    placement !== TICKET_STATUS_PLACEMENT.TICKET_CARD_WITH_MANAGER;

  let icon = <StyledIcon priority={priority} />;

  if (serverTitle === STATUSES.CLOSED) {
    titleLabel = isOption
      ? TITLES[EXTRA_STATUSES.CLOSE_ACTION]
      : TITLES[STATUSES.CLOSED];

    if (type === TICKET_TYPES.TOUR_SELECTION) {
      if (!isEmpty(orderIds)) {
        titleLabel = (
          <>
            {TITLES[EXTRA_STATUSES.CLOSED_BY_ORDER]}
            <StyledHighlightGreen>
              {orderIds && `№${orderIds?.join(', ')}`}
            </StyledHighlightGreen>
          </>
        );
      }

      if (closeReason) {
        titleLabel = (
          <>
            {TITLES[EXTRA_STATUSES.CLOSED_BY_REASON]}
            <StyledHighlightRed>{closeReason.label}</StyledHighlightRed>
          </>
        );
      }
    }

    icon = <StyledExplosionIcon isOption={isOption} />;
  }

  if (serverTitle === STATUSES.PLANNED) {
    titleLabel = planReason?.title || TITLES[STATUSES.PLANNED];
  }

  if (serverTitle === STATUSES.WAIT_FOR_SERVICE) {
    titleLabel =
      (planReason?.id && planReason?.title) ||
      TITLES[STATUSES.WAIT_FOR_SERVICE];
  }

  const isShownTime =
    time &&
    (serverTitle === STATUSES.CLOSED || serverTitle === STATUSES.PLANNED);

  return (
    <Bubble
      text={`Приоритет тикета: ${TRANSLATE_PRIORITIES[priority]}`}
      placement="bottom"
      disabled={isDisabledBubble}
      shouldStopPropagation={false}
    >
      <StyledTicketStatus className={className} {...props}>
        {icon}
        <StyledTitle title={String(titleLabel)}>{titleLabel}</StyledTitle>
        {isShownTime && (
          <>
            {statusTime && isAfter(statusTime, today) ? (
              <StyledTime>{formatDate(statusTime, TIME_FORMAT)}</StyledTime>
            ) : (
              <StyledTimeFrom time={time} />
            )}
          </>
        )}
      </StyledTicketStatus>
    </Bubble>
  );
};

const StyledTicketStatus = styled.div`
  align-items: center;
  display: flex;
  font-size: 14px;
  overflow: hidden;

  > * + * {
    margin-left: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const flicker = keyframes`
  0% {
    background-color: ${COLORS_BY_PRIORITY[PRIORITIES.CRITICAL]};
  }

  100% {
    background-color: transparent;
  }
`;

export const StyledIcon = styled.div<{ priority: ITicketStatus['priority'] }>`
  align-self: center;
  border-radius: 50%;
  flex-shrink: 0;
  height: 6px;
  width: 6px;
  margin: 1px 5px 0;

  ${({ priority }) => `background-color: ${COLORS_BY_PRIORITY[priority]};`}
  ${({ priority }) =>
    priority === PRIORITIES.CRITICAL &&
    css`
      animation: ${flicker} 1s linear infinite alternate;
    `}
`;

export const StyledTimeFrom = styled(TimeFrom)`
  color: #4d4d4d;
`;

const StyledExplosionIcon = styled(ExplosionIcon)<{
  isOption: ITicketStatus['isOption'];
}>`
  width: 16px;
  height: 17px;

  path {
    fill: ${({ isOption }) => (isOption ? '#D0021B' : '#919499')};
  }
`;

export const StyledTitle = styled.span``;

const StyledHighlightGreen = styled.span`
  color: #00c253;
  font-size: 12px;
  margin-left: 5px;
`;
const StyledHighlightRed = styled.span`
  color: #d0021b;
  font-size: 12px;
  margin-left: 5px;
`;

const StyledTime = styled.span`
  color: #4d4d4d;
  cursor: default;
  font-size: 11px;
`;

export default memo(TicketStatus);
